import type { PromptVariable } from '../types';

/** Valid inline variable reference: ${Name} where Name is [A-Za-z_][A-Za-z0-9_]* */
export const VARIABLE_PATTERN = /\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g;

/** Matches ${...} that is not a valid variable name (for syntax warnings) */
export const INVALID_VARIABLE_PATTERN = /\$\{([^}]*)\}/g;

export interface VariableValidationResult {
  usedVars: string[];
  undefinedVars: string[];
  unusedVars: string[];
  invalidSyntax: string[];
}

/**
 * Extract unique variable names referenced via ${Name} in text.
 * Only valid identifiers are returned.
 */
export function extractVariables(text: string): string[] {
  if (!text) return [];
  const names = new Set<string>();
  const regex = new RegExp(VARIABLE_PATTERN.source, 'g');
  for (const match of text.matchAll(regex)) {
    names.add(match[1]);
  }
  return Array.from(names);
}

/**
 * Replace ${Name} with values[Name] when present; leave unmatched references intact.
 */
export function substituteVariables(
  text: string,
  values: Record<string, string>
): string {
  if (!text) return text;
  return text.replace(
    new RegExp(VARIABLE_PATTERN.source, 'g'),
    (match, varName: string) =>
      Object.prototype.hasOwnProperty.call(values, varName) && values[varName] !== undefined
        ? values[varName]
        : match
  );
}

/**
 * Find ${...} occurrences that are not valid variable identifiers.
 */
export function findInvalidVariableSyntax(text: string): string[] {
  if (!text) return [];
  const invalid = new Set<string>();
  const validName = /^[A-Za-z_][A-Za-z0-9_]*$/;
  const regex = new RegExp(INVALID_VARIABLE_PATTERN.source, 'g');
  for (const match of text.matchAll(regex)) {
    const inner = match[1];
    if (!validName.test(inner)) {
      invalid.add(match[0]);
    }
  }
  return Array.from(invalid);
}

/**
 * Compare variables used in prompt text against defined metadata variables.
 */
export function validateVariables(
  promptText: string,
  definedVariables: PromptVariable[] = []
): VariableValidationResult {
  const usedVars = extractVariables(promptText);
  const definedNames = definedVariables.map((v) => v.name);
  const invalidSyntax = findInvalidVariableSyntax(promptText);

  return {
    usedVars,
    undefinedVars: usedVars.filter((v) => !definedNames.includes(v)),
    unusedVars: definedNames.filter((n) => !usedVars.includes(n)),
    invalidSyntax,
  };
}

/**
 * Validate across multiple prompt text parts (e.g. system + user).
 */
export function validatePromptVariables(
  texts: Array<string | undefined | null>,
  definedVariables: PromptVariable[] = []
): VariableValidationResult {
  const combined = texts.filter(Boolean).join('\n');
  return validateVariables(combined, definedVariables);
}

/**
 * Build default substitution values from variable metadata.
 */
export function defaultVariableValues(
  variables: PromptVariable[] = []
): Record<string, string> {
  const values: Record<string, string> = {};
  for (const v of variables) {
    if (v.default !== undefined && v.default !== null) {
      values[v.name] = String(v.default);
    } else if (v.type === 'enum' && v.options && v.options.length > 0) {
      values[v.name] = v.options[0];
    } else if (v.type === 'boolean') {
      values[v.name] = 'false';
    } else if (v.type === 'number' || v.type === 'range') {
      values[v.name] = v.min !== undefined ? String(v.min) : '0';
    } else {
      values[v.name] = '';
    }
  }
  return values;
}
